import {
  CookedEvent,
  EVENT_COOKED_CHANGE,
  EVENT_COOKED_FOCUS,
  EVENT_COOKED_REDACTED,
} from '@fullstory/client-api';
import { PiecewiseFunction } from '@fullstory/piecewise-function';
import { AppendOnlyArray, IterableList } from '@fullstory/util/collections';
import { memoize } from 'lodash';

// A list of cooked events. Redundant and unneeded events are automatically
// simplified away. This mirrors the simplifications we perform on the
// server side; we need to do it on the client side as well to support
// live sessions, for which we synthesize cooked events locally because
// the server hasn't had a chance to do so.
//
// Note that events are only simplified *within* batches sent to
// append(), sacrificing correctness somewhat to make the interface
// more predictable. TODO: Consider extending it to get the
// best of both words, e.g. by wrapping change events in promises.
export class CookedEventListModel extends AppendOnlyArray<CookedEvent> {
  private _eventsByTime = new PiecewiseFunction<CookedEvent>();

  constructor(initializer?: CookedEvent[]) {
    super();

    // NOTE: this method _can't_ call super(initializer) directly because the super constructor
    // calls `append()` before the `_timeline` member can be initialized and throws an error.
    // The same thing happens if the `constructor()` is omitted.
    if (initializer) {
      this.append(initializer);
    }
  }

  at(offsetMs: number): Readonly<CookedEvent> | undefined {
    return this._eventsByTime.at(offsetMs);
  }

  atOrAfter(offsetMs: number): Readonly<CookedEvent> | undefined {
    return this._eventsByTime.atOrAfter(offsetMs);
  }

  atOrBefore(offsetMs: number): Readonly<CookedEvent> | undefined {
    return this._eventsByTime.atOrBefore(offsetMs);
  }

  /**
   * CookedEventListModel overrides AppendOnlyList's `append` method to
   * remove redundant events.
   */
  append(sequence: IterableList<CookedEvent>): void {
    const filteredSequence: CookedEvent[] = [];

    let bufferedChangeEvent: Readonly<CookedEvent> | undefined = undefined;
    for (const event of sequence) {
      // When there's a sequence of change events, we only want to return the
      // last one. Hold on to the most recent one we've seen...
      if (
        event.Type === EVENT_COOKED_CHANGE &&
        continuesChangeEventSequence(event, bufferedChangeEvent)
      ) {
        bufferedChangeEvent = event;
        continue;
      }

      // ...and then add it, if we have one, once the run of change events
      // ends.
      if (bufferedChangeEvent !== undefined) {
        filteredSequence.push(bufferedChangeEvent);
        this._eventsByTime.add(
          bufferedChangeEvent,
          bufferedChangeEvent.When,
          bufferedChangeEvent.When + 1,
          bufferedChangeEvent.When,
        );
        bufferedChangeEvent = undefined;
      }

      // If there are two sequences of change events for different nodes
      // back-to-back, we may still have a change event here. If so, buffer
      // this change event and start coalescing the new sequence.
      if (
        event.Type === EVENT_COOKED_CHANGE &&
        continuesChangeEventSequence(event, bufferedChangeEvent)
      ) {
        bufferedChangeEvent = event;
        continue;
      }

      // Skip events we don't want to display.
      if (event.Type === EVENT_COOKED_FOCUS || event.Type === EVENT_COOKED_REDACTED) {
        continue;
      }

      filteredSequence.push(event);
      this._eventsByTime.add(event, event.When, event.When + 1, event.When);
    }

    super.append(filteredSequence);
  }
}

/** A memoized fallback when TargetSelector is missing but TargetSelectorStruct is provided. */
const extractTarget = memoize((event: Readonly<CookedEvent>): string | undefined => {
  if (event.TargetSelector === undefined && event.TargetSelectorStruct !== undefined) {
    return JSON.stringify(event.TargetSelectorStruct);
  }
  return event.TargetSelector;
});

/**
 * A change event continues a sequence of change events if it targets the
 * same element as the previous event in the sequence, or trivially if
 * there's no previous event.
 * TODO(sethf): This won't be fully accurate unless we start including node
 * IDs in cooked change events. We don't have enough info on the client side
 * to accurately construct target selectors, so the events we create in
 * findNfindNewDeadSpaceAndEvents() will never coalesce with pre-cooked
 * events.
 */
function continuesChangeEventSequence(
  event: Readonly<CookedEvent>,
  bufferedChangeEvent: Readonly<CookedEvent> | undefined,
): boolean {
  if (!bufferedChangeEvent) {
    return true;
  }

  return extractTarget(event) === extractTarget(bufferedChangeEvent);
}
